import { defineStore } from 'pinia'

import { reactive, ref } from 'vue'

import { AuthApi, RegisterType } from '@/features/api/services/authApi.js'
import { useAuthStore } from '@/features/auth/useAuthStore.js'
import {
  AlreadyRegisteredThroughGoogleMessage,
  useLoginStore,
} from '@/features/auth/views/login/useLoginStore.js'
import { useClubRegisterStore } from '@/features/clubRegister/views/useClubRegisterStore.js'
import { getActiveClubId } from '@/features/router/useActiveClub.js'
import { useClubRouter } from '@/features/router/useClubRouer.js'

export const useRegisterStore = defineStore('registerStore', function () {
  const authStore = useAuthStore()
  const loginStore = useLoginStore()
  const isLoading = ref(false)
  const needsOTP = ref(false)
  const errorText = ref('')

  const formValues = reactive({
    email: '',
    password: '',
    otpCode: '',
  })

  const router = useClubRouter()
  const clubRegisterStore = useClubRegisterStore()
  async function handlePasswordEnter() {
    errorText.value = ''
    if (needsOTP.value) {
      isLoading.value = true

      try {
        const loginResponse = await AuthApi.loginWithEmail({
          email: formValues.email,
          password: formValues.password,
          code: formValues.otpCode,
          tenantId: getActiveClubId(),
        })

        await authStore.setUserToken(loginResponse.auth_token, {
          email: formValues.email,
          password: formValues.password,
        })
        await loginStore.decideNavigation()
      } catch (e) {
        errorText.value = e?.response?.data
      } finally {
        isLoading.value = false
      }

      return
    }

    try {
      isLoading.value = true

      const registerResponse = await AuthApi.register({
        email: formValues.email,
        password: formValues.password,
        firstName: '',
        lastName: '',
        registerType: getActiveClubId() ? RegisterType.TenantUser : RegisterType.MemberTrial,
        tenantId: getActiveClubId(),
      })

      if (registerResponse?.includes?.('Failed_To_Register_User_Account_')) {
        console.error('Failed to register user account', registerResponse)
        errorText.value = 'Failed to register user account'
        return
      }

      if (registerResponse?.includes?.('User_Has_Been_Already_Registered')) {
        await router.push('/auth/login')
        return
      }

      if (registerResponse.codeSent) {
        needsOTP.value = true
        return
      }

      await authStore.setUserToken(registerResponse.auth_token, {
        email: formValues.email,
        password: formValues.password,
      })

      await loginStore.decideNavigation()
    } catch (e) {
      console.log(e)
      const errorData = e?.response?.data

      if (AlreadyRegisteredThroughGoogleMessage === errorData) {
        console.log('User has been already registered through google')

        clubRegisterStore.setAuthFlow(clubRegisterStore.AUTH_FLOW.LOGIN)
        loginStore.setCurrentStep(loginStore.STEPS.GOOGLE_LOGIN_WARNING)

        return
      }

      errorText.value = errorData
    } finally {
      isLoading.value = false
    }
  }

  function setEmail(email) {
    formValues.email = email
  }

  return {
    handlePasswordEnter,
    formValues,
    needsOTP,

    isLoading,
    setEmail,

    errorText,
  }
})
